import { ScrollText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useAdminAuditLog } from '@/api/hooks/useSuperadmin';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/utils';

export function AuditLogTab() {
  const [params, setParams] = useSearchParams();
  const eventName = params.get('aevent') ?? '';
  const slug = params.get('aslug') ?? '';
  const page = Number(params.get('apage') ?? '1');

  const debouncedEvent = useDebouncedValue(eventName, 300);
  const debouncedSlug = useDebouncedValue(slug, 300);

  const { data, isLoading, isFetching } = useAdminAuditLog({
    eventName: debouncedEvent || undefined,
    slug: debouncedSlug || undefined,
    page,
    limit: 50,
  });

  function updateParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'apage') next.delete('apage');
      return next;
    });
  }

  const logs = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by event…"
          value={eventName}
          onChange={(e) => updateParam('aevent', e.target.value)}
          className="max-w-48"
        />
        <Input
          placeholder="Filter by pharmacy slug…"
          value={slug}
          onChange={(e) => updateParam('aslug', e.target.value)}
          className="max-w-48"
        />
        <p className="text-sm text-muted-foreground">{data ? `${data.total} entr(ies)` : 'Loading…'}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <ScrollText className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No matching audit entries.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5" aria-busy={isFetching}>
          {logs.map((entry) => (
            <div key={entry.id} className="rounded-md border border-border px-3 py-2 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono font-medium text-foreground">{entry.event}</span>
                <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-muted-foreground">
                {entry.actor}
                {entry.actorRole && ` (${entry.actorRole})`}
                {entry.pharmacySlug && ` · ${entry.pharmacySlug}`}
                {entry.target && ` · target ${entry.target}`}
                {entry.ip && ` · ${entry.ip}`}
              </p>
              {entry.detail && <p className="mt-0.5 text-muted-foreground">{entry.detail}</p>}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateParam('apage', String(page - 1))}>
            Previous
          </Button>
          <span className="tabular text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => updateParam('apage', String(page + 1))}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}